import { type ReactNode, useEffect, useState } from 'react';

import {
  Button,
  type CheckboxProps,
  Flex,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import { IconTrash } from '@/components/Icons';

import { type FieldCommonProps } from '../FormField.types';
import { DraggableCheckboxList } from './DraggableCheckboxList';
import './creatableCheckBoxes.styles.css';
import { type CheckBoxOption } from './types';

export type SelectionType = 'single' | 'multiple';
type CreatableCheckboxesProps = {
  isCheckBoxDisabled?: boolean;
  selection: SelectionType;
  isRequired?: boolean;
};

export type { CheckBoxOption };

export type FieldCreatableCheckboxesProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'creatable-checkboxes';
  label?: ReactNode;
  helper?: ReactNode;
  options: Array<CheckBoxOption>;
  direction?: 'row' | 'column';
} & Pick<CheckboxProps, 'size'> &
  FieldCommonProps<TFieldValues, TName> &
  CreatableCheckboxesProps;

export const FieldCreatableCheckboxes = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldCreatableCheckboxesProps<TFieldValues, TName>
) => {
  const { t } = useTranslation(['interview', 'common']);
  const [editableOptions, setEditableOptions] = useState(props.options ?? []);
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  const validateAtLeastOneChecked = (options: Array<CheckBoxOption>) => {
    if (!options || options.length === 0)
      return t(
        'interview:createNewInterview.questionPreparation.atLeastOneOptionShouldBeSelected'
      );
    return (
      options.some((option) => option.checked) ||
      t(
        'interview:createNewInterview.questionPreparation.atLeastOneOptionShouldBeSelected'
      )
    );
  };

  const watchedOptions = useWatch({
    name: props.name,
    control: props.control,
  });
  // Syncing inner state (editableOptions) with react-hook-form's when there is a change outside of this component
  useEffect(() => {
    if (JSON.stringify(editableOptions) !== JSON.stringify(watchedOptions)) {
      const isOtherExists = watchedOptions?.some(
        (o: CheckBoxOption) => o.value === 'other'
      );
      if (isOtherExists) {
        setIsOtherSelected(true);
      }
      setEditableOptions(watchedOptions ?? editableOptions);
    }
  }, [editableOptions, watchedOptions]);

  return (
    <Controller
      {...props}
      rules={{
        validate:
          (props.isRequired ?? false)
            ? { atLeastOneChecked: validateAtLeastOneChecked }
            : {},
      }}
      render={({ field: { ref: _ref, onChange, ...field } }) => {
        const handleLabelChange = (index: number, newLabel: string) => {
          const updatedOptions = [...editableOptions];
          updatedOptions[index] = {
            ...updatedOptions[index],
            checked: updatedOptions[index]?.checked ?? false,
            label: newLabel,
          };
          setEditableOptions(updatedOptions);
          onChange(updatedOptions);
        };

        const handleCheckedChange = (
          value: string | undefined,
          isChecked: boolean
        ) => {
          let updatedOptions: Array<CheckBoxOption> = [];
          if (props.selection === 'single') {
            updatedOptions = editableOptions.map((option) =>
              option.value === value
                ? { ...option, checked: isChecked }
                : { ...option, checked: false }
            );
          }
          if (props.selection === 'multiple') {
            updatedOptions = editableOptions.map((option) =>
              option.value === value
                ? { ...option, checked: isChecked }
                : option
            );
          }
          setEditableOptions(updatedOptions);
          onChange(updatedOptions);
        };

        const addNewCheckbox = () => {
          const checkBoxCount = editableOptions.length;
          const newOptionValue = `option${checkBoxCount + 1}`;
          const newOption = {
            value: newOptionValue + new Date().getUTCMilliseconds(),
            label: `${checkBoxCount + 1}. ${t(
              'interview:createNewInterview.questionPreparation.option'
            )}`,
            checked: false,
          };
          setEditableOptions((prevOptions) => [...prevOptions, newOption]);
          onChange([...editableOptions, newOption]);
        };

        const addOtherOption = () => {
          setIsOtherSelected(true);
          const newOption = {
            value: 'other',
            label: t('common:other'),
            checked: false,
          };
          setEditableOptions((prevOptions) => [...prevOptions, newOption]);
          onChange([...editableOptions, newOption]);
        };

        const deleteCheckBox = (value: string) => {
          if (value === 'other') setIsOtherSelected(false);
          const valueDeletedEditableOptions = editableOptions.filter(
            (o) => o.value !== value
          );
          setEditableOptions(valueDeletedEditableOptions);
          onChange(valueDeletedEditableOptions);
        };

        const handleReorder = (startIndex: number, endIndex: number) => {
          const sortedOptions = editableOptions.sort((a, b) =>
            a.value === 'other' ? 1 : b.value === 'other' ? -1 : 0
          );
          const result = Array.from(sortedOptions);
          const [removed] = result.splice(startIndex, 1);
          result.splice(endIndex, 0, removed!);

          // Remove 'other' from the result and add it back at the end
          const otherOption = result.find((option) => option.value === 'other');
          const withoutOther = result.filter(
            (option) => option.value !== 'other'
          );
          const finalResult = otherOption
            ? [...withoutOther, otherOption]
            : withoutOther;

          setEditableOptions(finalResult);
          onChange(finalResult);
        };

        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
            <DraggableCheckboxList
              options={editableOptions}
              onReorder={handleReorder}
              onDelete={(option) => deleteCheckBox(option.value ?? '')}
              renderCheckbox={(option, index) => (
                <CheckboxInput
                  index={index}
                  option={option}
                  key={option.value}
                  deleteCheckBox={deleteCheckBox}
                  handleLabelChange={handleLabelChange}
                  handleCheckedChange={handleCheckedChange}
                  isCheckBoxDisabled={props.isCheckBoxDisabled ?? false}
                  isEditable={option.value !== 'other'}
                  checkBoxType={props.selection}
                  isDeletable={false}
                  {...field}
                />
              )}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="start"
              textAlign="start"
            >
              <label
                className={`custom-checkbox-${props.selection}`}
                style={{ flex: 0 }}
              >
                <input type="checkbox" checked={false} disabled />
                <span />
              </label>
              <Flex pb={1} alignItems="center" columnGap={1}>
                <Button
                  onClick={addNewCheckbox}
                  color="gray.500"
                  variant="unstyled"
                  boxShadow="none"
                >
                  {t(
                    'interview:createNewInterview.questionPreparation.addOption'
                  )}
                </Button>
                {!isOtherSelected && (
                  <>
                    <Text>{t('common:or')}</Text>
                    <Button
                      onClick={addOtherOption}
                      color="primary.500"
                      variant="unstyled"
                      boxShadow="none"
                    >
                      {t(
                        'interview:createNewInterview.questionPreparation.addOtherOption'
                      )}
                    </Button>
                  </>
                )}
              </Flex>
            </Stack>
            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};

type CheckboxInputProps = {
  index: number;
  isEditable: boolean;
  isDeletable: boolean;
  option: CheckBoxOption;
  checkBoxType: SelectionType;
  deleteCheckBox: (v: string) => void;
  handleLabelChange: (index: number, newLabel: string) => void;
  isCheckBoxDisabled: boolean;
  handleCheckedChange: (value: string | undefined, isChecked: boolean) => void;
};

const CheckboxInput: React.FC<CheckboxInputProps> = (props) => {
  return (
    <Stack direction="row" alignItems="center">
      <label className={`custom-checkbox-${props.checkBoxType}`}>
        <input
          type="checkbox"
          checked={
            props.checkBoxType === 'multiple' ? false : props.option.checked
          }
          onChange={(e) =>
            props.handleCheckedChange(props.option.value, e.target.checked)
          }
          disabled={props.isCheckBoxDisabled}
          value={props.option.value}
        />
        <span />
      </label>
      <Input
        value={props.option.label}
        onChange={(e) =>
          props.isEditable &&
          props.handleLabelChange(props.index, e.target.value)
        }
        width="100%"
        variant="unstyled"
        borderRadius="xl"
        mb={1}
        height={6}
        color={props.isEditable ? 'gray.900' : 'gray.500'}
        sx={{
          _focus: {
            borderBottomWidth: props.isEditable ? 1 : 0,
          },
        }}
        background="white"
        p={5}
        border="1px solid"
        borderColor="gray.100"
      />
      {props.isDeletable && (
        <Button
          variant="unstyled"
          onClick={() => props.deleteCheckBox(props.option.value ?? '')}
          boxShadow="none"
        >
          <IconTrash fontSize="20px" color="error.500" />
        </Button>
      )}
    </Stack>
  );
};
